/**
 * BoyceJobsPanel
 * -----------------------------------------------------------------------------
 * Shows the most-recent Boyce import jobs (run-all + invoices-only) with their
 * status, current step, error detail, and per-step progress.
 *
 * This is the production-debug tool: when an async job fails on prod, the
 * operator can immediately see WHICH step failed, the HTTP code, and the
 * actual response body — no need to dig through server logs.
 */
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  RefreshCw, History, CheckCircle2, AlertTriangle, Loader2, ChevronDown, ChevronRight, XCircle, Activity,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
  running:   { bg: 'bg-blue-50',    border: 'border-blue-300',    text: 'text-blue-700',    icon: Loader2,        spin: true  },
  completed: { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700', icon: CheckCircle2,   spin: false },
  failed:    { bg: 'bg-rose-50',    border: 'border-rose-300',    text: 'text-rose-700',    icon: AlertTriangle,  spin: false },
};

const STEP_LABELS = {
  customers: 'Customers',
  service_customers: 'Service Customers',
  vehicles: 'Vehicles',
  invoices: 'Invoices',
  cleanup: 'Auto-Cleanup',
  done: 'Done',
};

// Human-friendly labels for heartbeat stages (from _do_invoice_import)
const STAGE_LABELS = {
  parsing_csvs: 'Reading CSV files…',
  parsed_csvs: 'CSVs parsed',
  parsing_headers: 'Reading invoice headers…',
  parsed_headers: 'Invoice headers indexed',
  parsing_line_items: 'Reading invoice line items…',
  parsed_line_items: 'Line items indexed',
  indexed_headers_and_lines: 'Indexed invoice headers + line items',
  loaded_snapshots: 'Loaded service-customer & service-vehicle maps',
  loaded_lookups: 'Loaded customer & vehicle directories',
  counting_services: 'Counting service records…',
  counted_services: 'Service records counted',
  building_invoices: 'Linking invoices to customers & vehicles',
  inserting: 'Saving invoices to database',
  inserted: 'Final save complete',
  done: 'Done',
};

// Pretty labels for hb_* fields so the operator sees humanised counters
const HB_LABELS = {
  services_rows: 'Service rows parsed',
  headers_rows: 'Header rows parsed',
  line_items_rows: 'Line items parsed',
  header_sids: 'Indexed headers',
  line_sids: 'Indexed line groups',
  service_customer_snapshots: 'Service→Customer links',
  service_vehicle_snapshots: 'Service→Vehicle links',
  customers: 'Customer records loaded',
  vehicles: 'Vehicle records loaded',
  linked: 'Invoices linked',
  processed: 'Services processed',
  total: 'Total services',
  skipped_no_customer: 'Skipped (no customer)',
  skipped_no_vehicle: 'Skipped (no vehicle)',
  invoice_docs: 'Invoice docs ready',
  inserted: 'Invoices saved',
  batch: 'Current batch',
  batches_total: 'Total batches',
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const fmtDuration = (started, ended) => {
  if (!started) return '';
  const a = new Date(started).getTime();
  const b = ended ? new Date(ended).getTime() : Date.now();
  const sec = Math.max(0, Math.round((b - a) / 1000));
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
};

export default function BoyceJobsPanel() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const authHeader = () => {
    const session = localStorage.getItem('session_token');
    return session ? { Authorization: `Bearer ${session}` } : {};
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/super-admin/boyce-import/jobs?limit=10`, {
        headers: authHeader(), credentials: 'include',
      });
      if (!res.ok) {
        setJobs([]);
        return;
      }
      const data = await res.json();
      setJobs(Array.isArray(data.jobs) ? data.jobs : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh every 3 s while any job is still running so the operator
  // sees live heartbeat progress without needing to click Refresh.
  useEffect(() => {
    const hasRunning = jobs.some((j) => j.status === 'running');
    if (!hasRunning) return undefined;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
     
  }, [jobs]);

  const cancelJob = async (jobId) => {
    if (!window.confirm('Mark this job as cancelled? The background worker will not be killed but the UI will treat it as failed so you can retry.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/super-admin/boyce-import/jobs/${jobId}/cancel`, {
        method: 'POST', headers: authHeader(), credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Job marked as cancelled');
      await load();
    } catch (e) {
      toast.error(`Cancel failed: ${e.message || e}`);
    }
  };

  return (
    <Card className="p-5 border border-slate-200 bg-white" data-testid="boyce-jobs-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <History className="h-4 w-4 text-slate-500" />
          Recent Import Jobs
        </h3>
        <Button
          variant="outline" size="sm"
          onClick={load}
          disabled={loading}
          data-testid="boyce-jobs-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {jobs.length === 0 && !loading && (
        <p className="text-sm text-slate-500 mt-3" data-testid="boyce-jobs-empty">
          No import jobs yet. Run the Quick Start importer or Step 4 invoice importer above.
        </p>
      )}

      <div className="mt-3 space-y-2">
        {jobs.map((job) => {
          const tone = STATUS_TONE[job.status] || STATUS_TONE.running;
          const Icon = tone.icon;
          const isOpen = !!expanded[job.job_id];
          const isInvoicesOnly = job.job_type === 'invoices_only' || job.job_id?.startsWith('jobinv_');
          return (
            <div
              key={job.job_id}
              className={`border ${tone.border} ${tone.bg} rounded-md`}
              data-testid={`boyce-job-${job.job_id}`}
            >
              <button
                onClick={() => setExpanded((m) => ({ ...m, [job.job_id]: !isOpen }))}
                className="w-full px-3 py-2 flex items-center gap-3 text-left"
              >
                {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                <Icon className={`h-4 w-4 ${tone.text} ${tone.spin ? 'animate-spin' : ''}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-mono ${tone.text}`}>{job.job_id}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {isInvoicesOnly ? 'Invoices Only' : 'Bulk (5 steps)'}
                    </Badge>
                    <span className={`text-xs font-medium ${tone.text}`}>
                      {job.status === 'running'
                        ? `Running · ${STAGE_LABELS[job.current_stage] || job.current_stage || STEP_LABELS[job.current_step] || job.current_step}`
                        : job.status}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Started {fmtTime(job.started_at)} · {fmtDuration(job.started_at, job.completed_at)}
                  </p>
                </div>
              </button>

              {isOpen && (
                <div className="px-3 pb-3 pt-1 border-t border-slate-200 bg-white/60 space-y-2 text-xs">
                  {/* Live heartbeat for running jobs */}
                  {job.status === 'running' && (
                    <div className="border border-blue-200 bg-blue-50 rounded p-2 text-blue-900">
                      <p className="font-semibold flex items-center gap-1.5">
                        <Activity className="h-3.5 w-3.5" />
                        Live progress: {STAGE_LABELS[job.current_stage] || job.current_stage || job.current_step || '—'}
                      </p>
                      {/* Show all hb_* fields, humanised */}
                      {Object.entries(job).filter(([k]) => k.startsWith('hb_')).length > 0 && (
                        <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                          {Object.entries(job)
                            .filter(([k]) => k.startsWith('hb_'))
                            .map(([k, v]) => {
                              const key = k.replace(/^hb_/, '');
                              const label = HB_LABELS[key] || key.replace(/_/g, ' ');
                              return (
                                <div key={k}>
                                  <span className="text-blue-700">{label}:</span>{' '}
                                  <strong>{typeof v === 'number' ? v.toLocaleString() : String(v)}</strong>
                                </div>
                              );
                            })}
                        </div>
                      )}
                      <p className="text-[10px] text-blue-700 mt-1.5">
                        Last update {fmtTime(job.updated_at || job.started_at)}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 h-7 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50"
                        onClick={(e) => { e.stopPropagation(); cancelJob(job.job_id); }}
                        data-testid={`boyce-job-cancel-${job.job_id}`}
                      >
                        <XCircle className="h-3 w-3 mr-1" />
                        Mark as Cancelled / Stuck
                      </Button>
                    </div>
                  )}

                  {job.status === 'failed' && (
                    <div className="border border-rose-300 bg-rose-50 rounded p-2 text-rose-800">
                      <p className="font-semibold">Failed at step: {STEP_LABELS[job.failed_step] || job.failed_step || job.current_step || 'unknown'}</p>
                      {job.failed_http_code && (
                        <p className="mt-1">HTTP {job.failed_http_code}</p>
                      )}
                      {job.failed_response_body && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-rose-700">Response body</summary>
                          <pre className="mt-1 text-[10px] whitespace-pre-wrap break-all bg-white/70 p-2 rounded border border-rose-200 max-h-60 overflow-auto">
                            {job.failed_response_body}
                          </pre>
                        </details>
                      )}
                      {job.error && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-rose-700">Exception</summary>
                          <pre className="mt-1 text-[10px] whitespace-pre-wrap break-all bg-white/70 p-2 rounded border border-rose-200">
                            {job.error}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}

                  {/* Per-step progress */}
                  {job.progress && Object.keys(job.progress).length > 0 && (
                    <div className="space-y-1">
                      <p className="font-semibold text-slate-700">Per-step result:</p>
                      {Object.entries(job.progress).map(([step, val]) => (
                        <div key={step} className="border border-slate-200 rounded px-2 py-1.5 bg-white">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-slate-800">{STEP_LABELS[step] || step}</span>
                            <span className="text-[10px] text-slate-500">
                              {Object.entries(val || {}).slice(0, 5).map(([k, v]) =>
                                `${k}=${typeof v === 'object' ? '…' : v}`
                              ).join(' · ')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
